"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { Input } from "@/components/ui/input"
import { Check, Plus, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { FIELD_LABEL, FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { useGetContacts, usePostContacts, usePostContactsHiringManagers } from "openapi/api/endpoints"
import { toast } from "sonner"
import { asArray } from "@/lib/as-array"
import type { Contact as ContactModel } from "openapi/api/model"
import { getFriendlyContactCreateError } from "@/utils/contact-errors"

interface Contact {
    id: string
    name: string
    email: string
    position: string
    company_id: string
    company_name?: string
}

interface NewContact {
    id: string
    name: string
    email: string
    position: string
}

export interface CompanyContactSelectorProps {
    /** The job the picked or created contact is attached to. */
    jobId: string
    companyId: string
    selectedContacts: Array<{ id: string; name: string; email: string; position: string }>
    onAddContact:()=>void
}

export function CompanyContactSelector({ jobId, companyId, selectedContacts,onAddContact }: CompanyContactSelectorProps) {
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [newContact, setNewContact] = useState<NewContact>({
        id: "",
        name: "",
        email: "",
        position: "",
    })
    const nameInputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const {mutateAsync:assignToJob,isPending:isAssigning}=usePostContactsHiringManagers()
    const {mutateAsync:createContact,isPending}=usePostContacts()
    /*
      Attaching an existing contact never creates one, so the create mutation's
      pending state alone left the button live for the whole attach — a second
      click sent the attach twice.
    */
    const isAdding = isPending || isAssigning

    const { data, isLoading: isContactsLoading } = useGetContacts()

    const existingContacts = useMemo(
        () =>
            // A contact's email is optional; an absent one is "" here, since the
            // search lowercases every field and an `undefined` took the dialog down.
            asArray<ContactModel>(data).map((contact) => ({
                id: contact.id as string,
                name: [contact.first_name, contact.last_name].filter(Boolean).join(" "),
                email: contact.email ?? "",
                position: contact.title || '',
                company_id: contact.company_id!,
                company_name: contact.company_name ?? "",
            })) || [],
        [data],
    )

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                nameInputRef.current &&
                !nameInputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false)
            }
        }

        /*
          On click, not mousedown: the list sits in the flow and closing it
          moves everything below it up. Closed on mousedown, "Add manager"
          jumped out from under the pointer before the release, and the click
          that should have added the manager landed on nothing.
        */
        document.addEventListener("click", handleClickOutside)
        return () => document.removeEventListener("click", handleClickOutside)
    }, [])

    const filteredContacts = useMemo(() => {
        const query = newContact.name.trim().toLowerCase()
        const base = existingContacts.filter(
            (contact) =>
                !selectedContacts.some((sc) => (sc.id && sc.id === contact.id) || (!!sc.email && sc.email === contact.email)),
        )

        const sorted = [...base].sort((a, b) => {
            const aSameCompany = a.company_id === companyId
            const bSameCompany = b.company_id === companyId
            if (aSameCompany === bSameCompany) return 0
            return aSameCompany ? -1 : 1
        })

        if (!query) return sorted.slice(0, 6)

        return sorted
            .filter((contact) => {
                const name = contact.name.toLowerCase()
                const email = contact.email.toLowerCase()
                const position = contact.position.toLowerCase()
                const company = (contact.company_name ?? "").toLowerCase()
                return (
                    name.includes(query) ||
                    email.includes(query) ||
                    position.includes(query) ||
                    company.includes(query)
                )
            })
            .slice(0, 6)
    }, [existingContacts, selectedContacts, newContact.name, companyId])

    const handleSelectExistingContact = (contact: Contact) => {
        setNewContact({
            id: contact.id,
            name: contact.name,
            email: contact.email,
            position: contact.position,
        })
        setShowSuggestions(false)
    }

    /*
      A picked contact is attached as it is on record, so its email and title
      are shown, not edited: edits there used to be dropped silently, since
      only the contact's id is sent. The name is the way back to someone new.
    */
    const isPicked = Boolean(newContact.id)

    const handleAddContact = async () => {
        try{
            // An email is needed to create someone; attaching an existing
            // contact sends only its id, so one without an email can be added.
            if (!newContact.name || (!newContact.id && !newContact.email)) {
                toast.error(newContact.name ? "An email is needed to add someone new" : "A name is needed")
                return
            }

            let contactId=newContact.id

            if(newContact.id===""){
                const {id}=await createContact({data:{
                    first_name:newContact.name.split(" ")[0],
                    last_name:newContact.name.split(" ").slice(1).join(" ") || "",
                    email:newContact.email,
                    title:newContact.position,
                    company_id:companyId
                }})
                contactId=id
                // Kept before attaching: if the attach fails, a retry attaches
                // this contact rather than creating the person a second time.
                setNewContact((prev) => ({ ...prev, id }))
            }
            await assignToJob({data:{contact_id:contactId,job_id:jobId}})
            toast.success("Hiring manager added successfully")
            onAddContact()
            setNewContact({ name: "", email: "", position: "", id: "" })
        }catch(error){
            toast.error(getFriendlyContactCreateError(error))
        }


    }



    if (!companyId) {
        return (
            <div className="text-xs sm:text-sm text-muted-foreground p-3 sm:p-4 border border-dashed rounded-lg text-center">
                Please select a company first to add hiring managers
            </div>
        )
    }

    return (
        /*
          14e's language — "fields are underlines, not boxes" — and the app's
          own ramp. These were boxed inputs painted `bg-white … text-black`
          with a `dark:` twin, which is a second form language inside a dialog
          that had already adopted the first, and a colour picked against white
          rather than from the theme.
        */
        <div className="@container">
            <div className={FIELD_LABEL}>Add someone</div>
            <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-4 @sm:grid-cols-2">
                <div className="@sm:col-span-2">
                    <div className="relative">
                        <FieldShell label="Full name *">
                            <Input
                                ref={nameInputRef}
                                aria-label="Full name"
                                placeholder="Start typing to find a contact"
                                value={newContact.name}
                                onChange={(e) => {
                                    // Typing a name is someone new. A picked contact whose name was then
                                    // edited used to keep its id (attaching the original person), then its
                                    // email and title (creating the new person with someone else's
                                    // details) — so leaving a picked contact clears all it copied in.
                                    const name = e.target.value
                                    setNewContact((prev) =>
                                        prev.id ? { id: "", name, email: "", position: "" } : { ...prev, name },
                                    )
                                    setShowSuggestions(true)
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] pr-7")}
                            />
                        </FieldShell>
                        <ChevronDown className="pointer-events-none absolute right-1 bottom-2.5 h-4 w-4 text-muted-foreground" />
                    </div>
                    {isPicked && (
                        <p className="mt-1.5 text-[11px] text-muted-foreground">
                            From your contacts. Change the name to add someone new.
                        </p>
                    )}

                    {/*
                      In the flow, not floating: the list used to sit over the
                      Email field, which on a phone is the next thing to fill —
                      so it pushes the fields down while it is open.
                    */}
                    {showSuggestions && (
                        <div
                            ref={dropdownRef}
                            className="mt-1 max-h-[240px] w-full overflow-auto rounded-[10px] border bg-card"
                        >
                            {isContactsLoading ? (
                                <p className="p-3 text-xs text-muted-foreground">Loading contacts…</p>
                            ) : filteredContacts.length === 0 ? (
                                <p className="p-3 text-xs text-muted-foreground">No matching contacts found</p>
                            ) : (
                                <div className="p-1.5">
                                    {filteredContacts.map((contact) => (
                                        <button
                                            key={contact.id}
                                            type="button"
                                            onClick={() => handleSelectExistingContact(contact)}
                                            className="flex w-full items-start gap-2 rounded-md p-2 text-left transition-colors hover:bg-foreground/[0.06]"
                                        >
                                            <Check
                                                className={cn(
                                                    "mt-0.5 h-4 w-4 shrink-0",
                                                    // By id: emails are optional, and every contact without
                                                    // one matched an empty form by email.
                                                    newContact.id === contact.id ? "opacity-100" : "opacity-0",
                                                )}
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[12.5px] font-medium leading-[1.3]">
                                                    {contact.name}
                                                </span>
                                                <span className="mt-0.5 block truncate text-[11.5px] leading-[1.4] text-muted-foreground">
                                                    {[contact.email || "No email", contact.position, contact.company_name]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <FieldShell label="Email *">
                    <Input
                        aria-label="Email"
                        placeholder="name@company.com"
                        type="email"
                        value={newContact.email}
                        readOnly={isPicked}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, email: e.target.value }))}
                        className={cn(FIELD_UNDERLINE, "h-auto pb-[7px]", isPicked && "text-muted-foreground")}
                    />
                </FieldShell>
                <FieldShell label="Position">
                    <Input
                        aria-label="Position"
                        placeholder="Engineering Director"
                        value={newContact.position}
                        readOnly={isPicked}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, position: e.target.value }))}
                        className={cn(FIELD_UNDERLINE, "h-auto pb-[7px]", isPicked && "text-muted-foreground")}
                    />
                </FieldShell>

                <div className="@sm:col-span-2">
                    <PageAction
                        tone="primary"
                        disabled={isAdding}
                        onClick={handleAddContact}
                    >
                        <Plus className="size-3.5" />
                        {isAdding ? "Adding…" : "Add manager"}
                    </PageAction>
                </div>
            </div>
        </div>
    )
}